import React, { useState, useEffect } from 'react';

const ResponsiveTable = ({ content }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [tableData, setTableData] = useState(null);

  // Parse the structured table content
  useEffect(() => {
    const parseTableContent = (content) => {
      const lines = content.trim().split('\n');
      let title = '';
      let columns = [];
      const rows = [];

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        if (trimmedLine.startsWith('title:')) {
          title = trimmedLine.substring(6).trim();
        } else if (trimmedLine.startsWith('columns:')) {
          columns = trimmedLine.substring(8).split(',').map(col => col.trim());
        } else if (trimmedLine.includes(':')) {
          // Parse row data: "key: value1, value2, value3"
          const colonIndex = trimmedLine.indexOf(':');
          const key = trimmedLine.substring(0, colonIndex).trim();
          const values = trimmedLine.substring(colonIndex + 1).split(',').map(val => val.trim());
          rows.push([key, ...values]);
        }
      }

      return { title, columns, rows };
    };

    if (content) {
      setTableData(parseTableContent(content));
    }
  }, [content]);

  // Mobile carousel scroll handling
  const handleScroll = (e) => {
    if (window.innerWidth >= 768) return; // Only on mobile
    
    const container = e.target;
    if (!container) return;
    
    const carouselSlides = container.children.length;
    if (carouselSlides === 0) return;
    
    const slideWidth = container.scrollWidth / carouselSlides;
    const scrollLeft = container.scrollLeft;
    const newSlide = Math.round(scrollLeft / slideWidth);
    
    const clampedSlide = Math.max(0, Math.min(newSlide, carouselSlides - 1));
    
    if (clampedSlide !== currentSlide) {
      setCurrentSlide(clampedSlide);
    }
  };

  const scrollToSlide = (index) => {
    const container = document.querySelector('.table-mobile-carousel');
    if (!container) return;
    
    const carouselSlides = container.children.length;
    if (carouselSlides === 0) return;
    
    const slideWidth = container.scrollWidth / carouselSlides;
    const targetScroll = slideWidth * index;
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
    setCurrentSlide(index);
  };

  if (!tableData || !tableData.columns.length) {
    return null;
  }

  const { title, columns, rows } = tableData;

  // Create column cards data (skip first column as it's just the attribute names)
  const columnCards = columns.map((columnName, columnIndex) => ({
    title: columnName,
    items: rows.map(row => ({
      key: row[0], // First item is always the row label (attribute name)
      value: row[columnIndex + 1] || '' // Get value for this column (skip first column)
    }))
  }));

  return (
    <div className="my-14">
      {title && (
        <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
          {title}
        </h3>
      )}
      
      {/* Desktop Layout - Cards Side by Side */}
      <div className="hidden md:grid gap-6" style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}>
        {columnCards.map((card, index) => (
          <div key={index} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900 text-center">{card.title}</h4>
            </div>
            <div className="divide-y divide-gray-200">
              {card.items.map((item, itemIndex) => (
                <div key={itemIndex} className={`px-4 py-3 transition-colors duration-150 ${
                  itemIndex % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                }`}>
                  <div className="text-sm font-medium text-gray-500 mb-1">{item.key}</div>
                  <div className="text-sm text-gray-900">{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Layout - Carousel */}
      <div className="md:hidden">
        <div 
          className="table-mobile-carousel flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 pb-4"
          onScroll={handleScroll}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {columnCards.map((card, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden flex-shrink-0 w-full snap-center">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="font-semibold text-gray-900 text-center">{card.title}</h4>
              </div>
              <div className="divide-y divide-gray-200">
                {card.items.map((item, itemIndex) => (
                  <div key={itemIndex} className={`px-4 py-3 transition-colors duration-150 ${
                    itemIndex % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                  }`}>
                    <div className="text-sm font-medium text-gray-500 mb-1">{item.key}</div>
                    <div className="text-sm text-gray-900">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Dots Indicator */}
        {columnCards.length > 1 && (
          <div className="flex justify-center mt-4 space-x-2">
            {columnCards.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === currentSlide 
                    ? 'bg-gray-800'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponsiveTable; 